import { toLineItemType, type LineItemType } from '../constants/line-item-types';

/**
 * One of a tenant's positions (`positions`, API 0425 for `lineItemTypes`).
 *
 * Typed to the ACTUAL payload of `GET /api/v1/positions`, enveloped and
 * camelCase:
 *
 *     { success: true, data: [{ id, companyId, name, isStandard, isActive,
 *                              lineItemTypes, createdAt, updatedAt }] }
 *
 * NOT `trucking_positions` — that is a different table, managed on
 * Settings → Operations → Trucking (`TruckingPositionsSection`).
 *
 * A hand holds one PRIMARY position (`users.position_id`, `positionId`) and a
 * set they can work as (`user_positions`, `positionIds` — primary first).
 */
export interface Position {
  id: string;
  name: string;
  /** Platform-seeded: can't be renamed, deactivated or deleted (403). Its line item types CAN be set. */
  isStandard: boolean;
  isActive: boolean;
  /** The line item types a hand in this position works. The tenant sets them; nothing is seeded. */
  lineItemTypes: LineItemType[];
}

/**
 * Normalize one row. Tolerant of snake_case per the house rule. A line item
 * type this bundle does not know is dropped from the set rather than guessed
 * at — the API validates against the same enum, so that only happens across a
 * deploy boundary.
 */
export function toPosition(raw: any): Position | null {
  if (!raw || typeof raw !== 'object' || typeof raw.id !== 'string' || typeof raw.name !== 'string') return null;
  const rawTypes: unknown = raw.lineItemTypes ?? raw.line_item_types;
  const lineItemTypes = Array.isArray(rawTypes)
    ? rawTypes.map((t) => toLineItemType(String(t))).filter((t): t is LineItemType => t !== null)
    : [];
  return {
    id: raw.id,
    name: raw.name,
    isStandard: (raw.isStandard ?? raw.is_standard) === true,
    isActive: (raw.isActive ?? raw.is_active) !== false,
    lineItemTypes,
  };
}

/** Standard first, then alphabetical — the API's own order. */
export function comparePositions(a: Position, b: Position): number {
  if (a.isStandard !== b.isStandard) return a.isStandard ? -1 : 1;
  return a.name.localeCompare(b.name);
}

/**
 * Every position id a hand holds: `positionIds` (API 0425, primary first),
 * with the primary added if an older payload lacks it. `undefined` when the
 * payload carries neither — "unknown", which is NOT "holds none".
 */
export function heldPositionIds(member: any): string[] | undefined {
  const primary: string | null = member?.positionId ?? member?.position_id ?? null;
  const set: unknown = member?.positionIds ?? member?.position_ids;
  if (!Array.isArray(set)) return primary ? [primary] : undefined;
  return Array.from(new Set([...(primary ? [primary] : []), ...set.map(String)]));
}

/**
 * Wire shapes, hand-written from `positions.routes.ts` / `users.routes.ts` at
 * API 387c5d8 (#834). The response manifest recorded these writes only as 4xx
 * and the GET before `lineItemTypes`, so none can be typed from it.
 */
export interface PositionWire {
  id: string;
  companyId: string;
  name: string;
  isStandard: boolean;
  isActive: boolean;
  lineItemTypes: string[];
  createdAt: string;
  updatedAt: string;
}

/** DELETE /positions/:id — no `data`; `method` says which delete happened. */
export interface PositionDeleteResponse {
  success: boolean;
  message?: string;
  method?: 'hard_delete' | 'soft_delete';
  error?: string;
}

/** PUT /users/:id/positions → data. `positionIds` is primary first. */
export interface UserPositionsSet {
  id: string;
  positionId: string | null;
  positionIds: string[];
}
